import React, { useState } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import clinicService from '../../../../../services/clinic.service';
import { toast } from 'react-toastify';
function HoursOfOperationForm({ hours, onSave, onCancel }) {
    const { clinicId } = useParams();
    const [isSubmitting, setIsSubmitting] = useState(false);

    const {
        control,
        register,
        handleSubmit,
        formState: { errors },
    } = useForm({
        defaultValues: {
            hours: hours.map((hour) => ({
                ...hour,
                status: hour.status ? 'true' : 'false',
            })),
        },
    });

    const { fields } = useFieldArray({
        control,
        name: 'hours',
    });

    const onSubmit = async (data) => {
        setIsSubmitting(true);
        try {
            const payload = data.hours.map((hour) => ({
                ...hour,
                status: hour.status === 'true' || hour.status === true,
            }));

            const response = await clinicService.updateHours(clinicId, payload);
            onSave(response.data);
        } catch (err) {
            toast.error('Error updating hours. Please try again.', {
                position: toast.POSITION.BOTTOM_RIGHT,
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="notifications-panel">
            <div className="notifications-panel__header justify-content-between flex-wrap gap-2">
                <span className="d-flex align-items-center gap-2">
                    <i className="bi bi-pencil-square"></i>
                    Edit Hours of Operation
                </span>
                <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    onClick={onCancel}
                    disabled={isSubmitting}
                >
                    <i className="bi bi-x me-1"></i>
                    Cancel
                </button>
            </div>
            <div className="notifications-panel__body">
                <p className="notifications-form-hint mb-3">
                    Update all days below, then save your changes together.
                </p>

                <form onSubmit={handleSubmit(onSubmit)}>
                    <div className="table-responsive">
                        <table className="table table-bordered table-hover align-middle mb-0 process-queue-table clinic-hours-table">
                            <thead>
                                <tr>
                                    <th>
                                        <i className="bi bi-calendar3"></i> Day
                                    </th>
                                    <th>
                                        <i className="bi bi-watch"></i> Operating Hours
                                    </th>
                                    <th>
                                        <i className="bi bi-toggle-on"></i> Status
                                    </th>
                                    <th>
                                        <i className="bi bi-sticky"></i> Notes
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {fields.map((field, index) => (
                                    <tr key={field.id} className="clinic-hours-row">
                                        <td>
                                            <input type="hidden" {...register(`hours.${index}.id`)} />
                                            <input type="hidden" {...register(`hours.${index}.clinicId`)} />
                                            <input type="hidden" {...register(`hours.${index}.day`)} />
                                            <span className="clinic-hours-day">{field.day}</span>
                                        </td>
                                        <td>
                                            <div className="input-group input-group-sm">
                                                <input
                                                    type="text"
                                                    className={`form-control ${
                                                        errors.hours?.[index]?.start ? 'is-invalid' : ''
                                                    }`}
                                                    placeholder="Start (e.g., 08:00 AM)"
                                                    {...register(`hours.${index}.start`, {
                                                        validate: (value, formValues) => {
                                                            const status = formValues.hours?.[index]?.status;
                                                            if (status === 'true' && !value?.trim()) {
                                                                return 'Start time is required when open';
                                                            }
                                                            return true;
                                                        },
                                                    })}
                                                />
                                                <span className="input-group-text">to</span>
                                                <input
                                                    type="text"
                                                    className={`form-control ${
                                                        errors.hours?.[index]?.end ? 'is-invalid' : ''
                                                    }`}
                                                    placeholder="End (e.g., 05:00 PM)"
                                                    {...register(`hours.${index}.end`, {
                                                        validate: (value, formValues) => {
                                                            const status = formValues.hours?.[index]?.status;
                                                            if (status === 'true' && !value?.trim()) {
                                                                return 'End time is required when open';
                                                            }
                                                            return true;
                                                        },
                                                    })}
                                                />
                                            </div>
                                            {(errors.hours?.[index]?.start || errors.hours?.[index]?.end) && (
                                                <div className="text-danger small mt-1">
                                                    {errors.hours[index]?.start?.message ||
                                                        errors.hours[index]?.end?.message}
                                                </div>
                                            )}
                                        </td>
                                        <td>
                                            <select
                                                className="form-select form-select-sm"
                                                {...register(`hours.${index}.status`)}
                                            >
                                                <option value="true">Open</option>
                                                <option value="false">Closed</option>
                                            </select>
                                        </td>
                                        <td>
                                            <input
                                                type="text"
                                                className="form-control form-control-sm"
                                                placeholder="Add note (optional)"
                                                {...register(`hours.${index}.note`)}
                                            />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="clinic-hours-form-actions">
                        <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm"
                            onClick={onCancel}
                            disabled={isSubmitting}
                        >
                            Cancel
                        </button>
                        <button type="submit" className="btn btn-success btn-sm" disabled={isSubmitting}>
                            {isSubmitting ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                                    Saving…
                                </>
                            ) : (
                                <>
                                    <i className="bi bi-floppy me-1"></i>
                                    Save Changes
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default HoursOfOperationForm;
